import React, { useState, useEffect, useContext, useMemo } from "react";
import { Box } from "@mui/material";
import { WidgetContext } from "../../store/widgetContext";
import { LAUNCHER_BUBBLE_SIZES } from "../../constants/settings";
import { get } from "lodash";
import { useWidgetLogic } from "../../hooks/widget/useWidgetLogic";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";
import { getVerifiedParentOrigin, postToParent } from "../../routes/host-bridge";
import Conversations from "./Conversations";
import Tickets from "./Tickets";
import Articles from "./Articles";
import Chat from "./Chat";
import Voice from "./Voice";
import Navigation from "./Navigation";
import Document from "./Document";
import Home from "./Home";
import Header from "./Header";
import StartActions from "./StartActions";

export default function Widget({ ...props }) {
	const { widget, conversations, contact } = useContext(WidgetContext);
	const { widgetState, handlers } = useWidgetLogic();
	const { handleNavigate, setSelectedDocument, setSelectedCollection } = handlers;
	const [searchQuery, setSearchQuery] = useState("");
	const [isSmallScreen, setIsSmallScreen] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const { msg, lang, isRtl } = useWidgetMsg();
	const { isVoiceChatEnabled, isTextChatEnabled } = widgetState;
	const initialMode = isTextChatEnabled && !isVoiceChatEnabled ? "chat" : isVoiceChatEnabled && !isTextChatEnabled ? "voice" : "chat";
	const [chatMode, setChatMode] = useState(initialMode);

	useEffect(() => {
		const listener = (event) => {
			const verified = getVerifiedParentOrigin();
			if (verified && event.origin !== verified) return;
			if (typeof event.data?.isSmallScreen === "boolean") {
				setIsSmallScreen(event.data.isSmallScreen);
			}
			if (event.data?.type === "fullscreenState" && typeof event.data?.isFullscreen === "boolean") {
				setIsFullscreen(event.data.isFullscreen);
			}
		};
		window.addEventListener("message", listener);
		postToParent({ type: "requestScreenSize" });

		return () => window.removeEventListener("message", listener);
	}, []);

	const width = get(widget, "chat.dimensions.width", 370);
	const height = get(widget, "chat.dimensions.height", 604);
	const buttonSize = get(widget, "chat.launcher.bubbleSize", "medium");
	const buttonHeight = LAUNCHER_BUBBLE_SIZES[buttonSize] || 60;

	const isMobile = isSmallScreen && (window.matchMedia("(pointer: coarse)").matches || window.matchMedia("(hover: none)").matches);

	const wrapperStyle = useMemo(() => ({
		position: "absolute",
		right: 0,
		...(isFullscreen || isSmallScreen
			? { width: "100%", height: "100%", bottom: 0 }
			: { bottom: `${buttonHeight + 16}px` }
		)
	}), [isSmallScreen, isFullscreen, buttonHeight]);

	const containerStyle = useMemo(() => {
		const gutter = buttonHeight + 16;
		const heightValue = isFullscreen 
			? `calc(100% - ${gutter}px)` 
			: (isMobile ? "100dvh" : isSmallScreen ? `calc(100dvh - ${gutter}px)` : `${height}px`);

		return {
			width: isFullscreen || isSmallScreen ? "100%" : `${width}px`,
			height: heightValue,
			maxWidth: "100%",
			maxHeight: isFullscreen 
				? `calc(100% - ${gutter}px)` 
				: (isMobile ? "100dvh" : `calc(100dvh - ${gutter}px)`),
			position: "relative",
			background: "white",
			boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)",
			overflow: "hidden",
			borderRadius: isMobile ? "0" : "16px",
			display: "flex",
			flexDirection: "column",
		};
	}, [isSmallScreen, isMobile, isFullscreen, width, height, buttonHeight]);

	const renderContent = () => {
		const { isVoiceChatEnabled, isTextChatEnabled } = widgetState;

		switch (widgetState.location) {
		case "conversations":
			return (
				<Conversations
					isVoiceChatEnabled={isVoiceChatEnabled}
					handleNavigate={handleNavigate}
					chatMode={chatMode}
					botId={props.botId}
					tenantId={props.tenantId}
					projectUUID={props.projectUUID}
				/>
			);
		case "tickets":
			return (
				<Tickets handleNavigate={handleNavigate} searchQuery={searchQuery} />
			);
		case "articles":
		case "collection":
			return (
				<Articles
					handleNavigate={handleNavigate}
					setSelectedDocument={setSelectedDocument}
					setSelectedCollection={setSelectedCollection}
					searchQuery={searchQuery}
					selectedCollection={widgetState.selectedCollection}
					uuid={props.projectUUID}
					botId={props.botId}
					tenantId={props.tenantId}
				/>
			);
		case "document":
			return (
				<Document selectedDocument={widgetState.selectedDocument} uuid={props.projectUUID} tenantId={props.tenantId} botId={props.botId} />
			);
		case "newConversation":
			const latestNonPlaceholder = !widgetState.conversationId
				? conversations?.filter(c => c.placeholder !== true && c.status !== "Resolved").sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
				: null;
			const finalConversationId = widgetState.conversationId || conversations?.find((conv) => conv.placeholder === true && conv.status !== "Resolved")?._id || latestNonPlaceholder?._id || null;
			return (
				<Chat
					conversationId={finalConversationId}
					initialAssignedAgents={widgetState.assignedAgents || []}
					handleNavigate={handleNavigate}
					setSelectedDocument={setSelectedDocument}
					setSelectedCollection={setSelectedCollection}
					{...props}
				/>
			);
		case "newVoiceChat":
			const voiceConversationId = widgetState.conversationId || conversations?.find((conv) => conv.placeholder === true)?._id || null;
			return (
				<Voice
					conversationId={voiceConversationId}
					handleNavigate={handleNavigate}
					{...props}
				/>
			);
		case "home":
		default:
			return (
				<Home
					handleNavigate={handleNavigate}
					setSelectedDocument={setSelectedDocument}
					setSelectedCollection={setSelectedCollection}
					isTextChatEnabled={isTextChatEnabled}
					isVoiceChatEnabled={isVoiceChatEnabled}
				/>
			);
		}
	};

	useEffect(() => {
		if (widgetState.location !== "tickets" && widgetState.location !== "articles" && widgetState.location !== "collection") {
			setSearchQuery("");
		}
	}, [widgetState.location]);

	useEffect(() => {
		postToParent({ type: "locationChange", location: widgetState.location });
	}, [widgetState.location]);

	useEffect(() => {
		const newInitialMode = isTextChatEnabled && !isVoiceChatEnabled ? "chat" : isVoiceChatEnabled && !isTextChatEnabled ? "voice" : "chat";
		setChatMode(newInitialMode);
	}, [isTextChatEnabled, isVoiceChatEnabled]);

	const showStartActions = widgetState.location === "conversations" && (widgetState.isTextChatEnabled || widgetState.isVoiceChatEnabled);

	return (
		<Box sx={wrapperStyle}>
			<Box sx={containerStyle} dir={isRtl ? "rtl" : "ltr"} lang={lang} data-widget-panel="">
				<Header
					widgetState={widgetState}
					handlers={handlers}
					userName={contact?.meta?.["Full name"] || ""}
					searchQuery={searchQuery}
					onSearchChange={(e) => setSearchQuery(e.target.value)}
					widgetHeight={height}
					selectedDocument={widgetState.selectedDocument}
					selectedCollection={widgetState.selectedCollection}
					chatMode={chatMode}
					onChatModeChange={setChatMode}
					isFullscreen={isFullscreen}
					isSmallScreen={isSmallScreen}
				/>
				<Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
					{renderContent()}
				</Box>
				{showStartActions && (
					<Box
						sx={{
							position: "absolute",
							left: 10,
							right: 10,
							bottom: 67,
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							pointerEvents: "none",
						}}
					>
						<Box sx={{ pointerEvents: "auto", width: "100%", maxWidth: 520, margin: "0 auto" }}>
							<StartActions
								variant="glass"
								onStartChat={() => handleNavigate({ location: "newConversation" })}
								onStartCall={() => handleNavigate({ location: "newVoiceChat" })}
								showChat={widgetState.isTextChatEnabled}
								showCall={widgetState.isVoiceChatEnabled}
								startChatLabel={msg("home.start_new_chat", "Start a new chat")}
								startCallLabel={msg("home.start_new_call", "Start a new call")}
							/>
						</Box>
					</Box>
				)}
				{widgetState.location !== "document" && widgetState.location !== "collection" && widgetState.location !== "newConversation" && widgetState.location !== "newVoiceChat" && (
					<Navigation handleNavigate={handleNavigate} currentLocation={widgetState.location || "home"} />
				)}
			</Box>
		</Box>
	);
}
